import app from 'app';
import moment from 'moment';
import _ from 'lodash';

function SubmitClaimService(Api) {
  const planTypesOrder = ['medical', 'pharmacy', 'dental', 'vision'];
  const thisYear = moment().year();
  const lastYear = moment().subtract(1, 'year').year();

  function isThisYearOrLastYear(year) {
    return year === thisYear || year === lastYear;
  }

  function hasMedicalMembershipFromLastTwoYears(person) {
    return _.find(person.planMemberships, (x) => {
      return (
        x.plan.type === 'medical' &&
        isThisYearOrLastYear(moment(x.startDate).year())
      );
    });
  }

  function parseDependent(dependent) {
    return {
      patientId: dependent.id,
      name: dependent.firstName,
      dateOfBirth: dependent.dateOfBirth,
    };
  }

  function getMedicalDependents(dependents) {
    return dependents
      .filter(hasMedicalMembershipFromLastTwoYears)
      .map(parseDependent);
  }

  function getClaimFormLink(claimForms, documentType) {
    const claimForm = _.find(claimForms, { documentType });
    return claimForm ? claimForm.url : '';
  }

  function getMostRecentPlanOfType(planMembershipsOfType) {
    return _(planMembershipsOfType)
      .sortBy((x) => moment(x.startDate))
      .last();
  }

  function getMostRecentPlanOfEachType(planMemberships) {
    return _(planMemberships)
      .filter((x) => isThisYearOrLastYear(moment(x.startDate).year()))
      .groupBy('plan.type')
      .mapValues(getMostRecentPlanOfType)
      .value();
  }

  function parseDentalOrVision(planMembership) {
    return {
      claimFormLink: getClaimFormLink(
        planMembership.plan.claimForms,
        'outOfNetworkClaimForm'
      ),
      networkName: planMembership.plan.networkName,
      type: planMembership.plan.type,
    };
  }

  // expand claimForms
  function getPlans(personId, sponsorId) {
    return Api.req({
      endPoint: `/person/${personId}`,
      cache: true,
      params: {
        expand: [
          'claimForms',
          'dependents',
          'planMemberships',
          'plan',
          'submittedPharmacyClaims',
          'url',
        ],
        sponsorIds: [sponsorId],
        'plan.isCHPlan': true,
      },
    }).then((person) => {
      const plans = {};
      const mostRecentPlanOfEachType = getMostRecentPlanOfEachType(
        person.planMemberships
      );

      if (mostRecentPlanOfEachType.medical) {
        plans.medical = {
          dependents: getMedicalDependents(person.dependents),
          importCode: mostRecentPlanOfEachType.medical.plan.importCode,
          networkName: mostRecentPlanOfEachType.medical.plan.networkName,
          submitterId: person.id,
          type: 'medical',
        };
      }

      if (mostRecentPlanOfEachType.dental) {
        plans.dental = parseDentalOrVision(mostRecentPlanOfEachType.dental);
      }

      if (mostRecentPlanOfEachType.vision) {
        plans.vision = parseDentalOrVision(mostRecentPlanOfEachType.vision);
      }

      if (mostRecentPlanOfEachType.pharmacy) {
        plans.pharmacy = {
          claimFormLink: getClaimFormLink(
            mostRecentPlanOfEachType.medical.plan.submittedPharmacyClaims,
            'submitAPharmacyClaim'
          ),
          networkName: mostRecentPlanOfEachType.pharmacy.plan.networkName,
          type: 'pharmacy',
        };
      }

      return plans;
    });
  }

  function getSortedPlanTypes(plans) {
    return _.intersection(planTypesOrder, Object.keys(plans));
  }

  function getCOBText(cob) {
    return `Do you have another insurance outside of Collective Health? ${
      cob ? 'Yes' : 'No'
    }`;
  }

  function getFamilyMemberText(otherMember) {
    return `${otherMember ? 'Claim is for other family member' : ''}`;
  }

  function submitClaim(files, submitterId, patientId, cob) {
    const otherMember = patientId === 0;
    const cobText = getCOBText(cob);
    const otherMemberText = getFamilyMemberText(otherMember);

    if (otherMember) {
      patientId = submitterId;
    }
    return Api.req({
      endPoint: files[0].isZendesk
        ? '/crmclient/msoon/submit_request'
        : '/submitted_claim',
      method: 'POST',
      timeout: 30000,
      data: {
        channel: '11106',
        patientId,
        submitterId,
        imagePaths: files.map((file) => file.pathKey),
        text: [cobText, otherMemberText].join('\n'),
        hasOtherInsurance: cob,
      },
    });
  }

  return {
    getPlans,
    getSortedPlanTypes,
    submitClaim,
  };
}

app.service('SubmitClaimService', SubmitClaimService);
